import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Send, Search, MessageCircle } from 'lucide-react';
import * as api from '../../services/api';
import './Messages.css';

const card = { background: 'rgba(30,41,59,0.3)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '1rem' };
const input = { width: '100%', padding: '0.75rem 1rem', borderRadius: '0.75rem', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', outline: 'none' };
const btn = { padding: '0.75rem 1.5rem', background: 'linear-gradient(to right, #10b981, #06b6d4)', color: '#020617', fontWeight: 600, borderRadius: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' };

export default function Messages() {
  const { projects, fetchProjects, user } = useApp();
  const [search, setSearch] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [newFile, setNewFile] = useState(null);
  const [isInternalMessage, setIsInternalMessage] = useState(true);
  const [messageView, setMessageView] = useState('all'); // all | internal | client
  const [loading, setLoading] = useState(false);

  const projectsWithConversations = projects || [];
  const filtered = projectsWithConversations.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.clientName?.toLowerCase().includes(search.toLowerCase())
  );

  const selectedProject = selectedProjectId
    ? projectsWithConversations.find((p) => p._id === selectedProjectId)
    : null;

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (selectedProjectId) loadMessages();
  }, [selectedProjectId]);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const data = await api.getProjectMessages(selectedProjectId);
      const list = data || [];
      setMessages(list);

      // Mark incoming unread messages as read when conversation is opened.
      const unreadIncoming = list.filter((m) => {
        if (m.isRead) return false;
        if (user?.role === 'staff') return m.sender !== 'staff';
        return m.sender === 'client';
      });
      if (unreadIncoming.length) {
        await Promise.allSettled(unreadIncoming.map((m) => api.markMessageRead(m._id)));
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedProjectId) return;

    const messageText = newMessage;
    setNewMessage('');
    const fileToSend = newFile;
    setNewFile(null);

    try {
      let msg;
      if (fileToSend) {
        const fd = new FormData();
        fd.append('file', fileToSend);
        fd.append('project', selectedProjectId);
        fd.append('client', selectedProject?.client || '');
        fd.append('content', messageText);
        fd.append('sender', 'admin');
        fd.append('visibility', isInternalMessage ? 'internal' : 'client');
        msg = await api.sendMessage(fd);
      } else {
        msg = await api.sendMessage({
          project: selectedProjectId,
          client: selectedProject?.client,
          content: messageText,
          sender: 'admin',
          visibility: isInternalMessage ? 'internal' : 'client'
        });
      }
      setMessages((prev) => [...prev, msg]);
    } catch (err) {
      alert('Failed to send message');
      setNewMessage(messageText);
      setNewFile(fileToSend);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await api.deleteMessage(messageId);
      setMessages((prev) => prev.filter((m) => m._id !== messageId));
    } catch (err) {
      alert('Failed to delete message');
    }
  };

  const isStaffUser = user?.role === 'staff';
  const visibleMessages = (messages || []).filter((msg) => {
    if (messageView === 'internal') return msg.visibility === 'internal';
    if (messageView === 'client') return msg.visibility !== 'internal';
    return true;
  });
  const internalCount = (messages || []).filter((m) => m.visibility === 'internal').length;
  const clientCount = (messages || []).filter((m) => m.visibility !== 'internal').length;

  return (
    <div className="messages-page">
      <div className="messages-head">
        <h1>{isStaffUser ? 'Project Messages' : 'Project Messages'}</h1>
        <span>{projectsWithConversations.length} conversations</span>
      </div>

      <div className="messages-layout">
        <div style={{ ...card, padding: '1rem', display: 'flex', flexDirection: 'column' }}>
          <div style={{ position: 'relative', marginBottom: '1rem' }}>
            <Search style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', width: '1.25rem', height: '1.25rem', color: '#64748b' }} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search conversations..." style={{ ...input, paddingLeft: '3rem', fontSize: '0.875rem' }} />
          </div>
          <div className="messages-conversation-list">
            {filtered.length === 0 ? (
              <div className="messages-empty-state">
                <MessageCircle style={{ width: '2rem', height: '2rem', margin: '0 auto 1rem', opacity: 0.5 }} />
                <p>No projects with clients yet</p>
              </div>
            ) : (
              filtered.map((project) => (
                <button
                  key={project._id}
                  onClick={() => setSelectedProjectId(project._id)}
                  className="messages-conversation-btn"
                  style={{
                    background: selectedProjectId === project._id
                      ? 'linear-gradient(to right, rgba(16,185,129,0.2), rgba(6,182,212,0.2))'
                      : 'rgba(148,163,184,0.05)',
                    border: selectedProjectId === project._id
                      ? '1px solid rgba(16,185,129,0.3)'
                      : '1px solid rgba(255,255,255,0.05)',
                    color: selectedProjectId === project._id ? '#34d399' : '#e2e8f0',
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{project.name}</div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.25rem' }}>{project.clientName || 'No client'}</div>
                </button>
              ))
            )}
          </div>
        </div>

        <div style={{ ...card, padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
          {selectedProject ? (
            <>
              <div className="messages-project-head">
                <h2>{selectedProject.name}</h2>
                <p>with {selectedProject.clientName || 'Client'}</p>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                {[
                  { key: 'all', label: `All (${messages.length})` },
                  { key: 'internal', label: `Team (${internalCount})` },
                  { key: 'client', label: `Client (${clientCount})` },
                ].map((item) => (
                  <button
                    key={item.key}
                    onClick={() => setMessageView(item.key)}
                    style={{
                      padding: '0.35rem 0.7rem',
                      borderRadius: '999px',
                      fontSize: '0.78rem',
                      border: '1px solid rgba(255,255,255,0.12)',
                      background: messageView === item.key ? 'rgba(16,185,129,0.2)' : 'rgba(148,163,184,0.08)',
                      color: messageView === item.key ? '#34d399' : '#94a3b8'
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <div className="messages-stream">
                {loading ? (
                  <div className="messages-empty-state">Loading messages...</div>
                ) : visibleMessages.length === 0 ? (
                  <div className="messages-empty-state">
                    <MessageCircle style={{ width: '2rem', height: '2rem', margin: '0 auto 1rem', opacity: 0.5 }} />
                    <p>No {messageView === 'all' ? '' : messageView === 'internal' ? 'team ' : 'client '}messages yet.</p>
                  </div>
                ) : (
                  visibleMessages.map((msg) => (
                    <div key={msg._id} style={{ marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: msg.sender === 'admin' ? 'flex-end' : 'flex-start' }}>
                        <div
                          className="messages-bubble"
                          style={{
                            background: (msg.sender === 'admin' || msg.sender === 'staff') ? 'rgba(16,185,129,0.15)' : 'rgba(148,163,184,0.1)',
                            border: (msg.sender === 'admin' || msg.sender === 'staff') ? '1px solid rgba(16,185,129,0.2)' : '1px solid rgba(255,255,255,0.1)',
                          }}
                        >
                          <div style={{ fontSize: '0.875rem', color: (msg.sender === 'admin' || msg.sender === 'staff') ? '#34d399' : '#94a3b8', fontWeight: 500, marginBottom: '0.25rem' }}>
                            {(msg.sender === 'admin' || msg.sender === 'staff') ? 'You' : msg.senderName || 'Client'}
                          </div>
                          <div style={{ color: '#e2e8f0', wordBreak: 'break-word' }}>{msg.content}</div>
                          {msg.visibility === 'internal' && (
                            <div style={{ marginTop: '0.35rem', fontSize: '0.7rem', color: '#fbbf24' }}>Internal team message</div>
                          )}
                          {msg.attachment && (
                            <div style={{ marginTop: '0.5rem' }}>
                              <a href={msg.attachment.signedUrl || (msg.attachment.url && (window.location.origin + '/uploads/' + msg.attachment.url)) || '#'} target="_blank" rel="noreferrer" style={{ color: '#34d399' }}>
                                {msg.attachment.originalName}
                              </a>
                            </div>
                          )}
                          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.5rem' }}>{new Date(msg.createdAt).toLocaleString()}</div>
                          {msg.sender === 'admin' && (
                            <button onClick={() => handleDeleteMessage(msg._id)} className="messages-delete-btn">Delete</button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="messages-composer">
                <div className="messages-input-row">
                  <input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Type a message..."
                    style={{ ...input, flex: 1 }}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() && !newFile}
                    style={{ ...btn, opacity: newMessage.trim() || newFile ? 1 : 0.5, cursor: newMessage.trim() || newFile ? 'pointer' : 'not-allowed' }}
                  >
                    <Send style={{ width: '1rem', height: '1rem' }} />
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <input id="internal-message-toggle" type="checkbox" checked={isInternalMessage} onChange={(e) => setIsInternalMessage(e.target.checked)} />
                  <label htmlFor="internal-message-toggle" style={{ color: '#94a3b8', fontSize: '0.82rem' }}>Internal team message (hidden from client)</label>
                </div>
                <div className="messages-file-row">
                  <input type="file" onChange={(e) => setNewFile(e.target.files[0])} />
                  <div style={{ color: '#94a3b8' }}>{newFile ? newFile.name : 'No file selected'}</div>
                </div>
              </div>
            </>
          ) : (
            <div className="messages-empty-state messages-empty-full">
              <MessageCircle style={{ width: '3rem', height: '3rem', marginBottom: '1rem', opacity: 0.3 }} />
              <p>Select a conversation to start messaging</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
